import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useState } from 'react';

import { STATISTIC_CONST } from '@/constant';
import {
  useMyTotalFrameStateQuery,
  useMyTotalTaskStateQuery,
  useMyWorkProjectPeriodMetricQuery,
  useMyWorkTotalObjectQuery,
} from '@/hook';
import {
  CenterFocusWeak,
  History,
  VFlex,
  VStack,
  VTab,
  VTabTrigger,
  VTabTriggerContainer,
  VideoLabel,
} from '@/package';
import type { TaskStateStatistic } from '@/type';

import {
  calculatePeriodData,
  calculateTotalObject,
  filterUnknownClass,
} from '../Statistic';
import { BarChart } from '../Statistic/BarChart';
import { DoughnutChart } from '../Statistic/DoughnutChart';
import { RangePicker } from '../Statistic/RangePicker';
import type { PeriodUnit } from '../Statistic/type';

const MyWorkProjectStatistics = () => {
  return (
    <VStack gap={1.5} className="h-full w-full overflow-y-auto bg-white p-4">
      <MyWorkProjectStatisticOverview />
      <MyWorkProjectPeriodLabeledObject />
    </VStack>
  );
};

const MyWorkProjectStatisticOverview = () => {
  const { data: totalFrameState } = useMyTotalFrameStateQuery();
  const { data: totalTaskState } = useMyTotalTaskStateQuery();
  const { data: projectObject } = useMyWorkTotalObjectQuery(true);

  const totalTask = totalTaskState?.total ?? 0;
  const totalObject = calculateTotalObject(projectObject?.statExtendClass);
  const totalFrame = totalFrameState?.total ?? 0;
  const taskProgress = {
    none: (totalTaskState as TaskStateStatistic)?.none ?? 0,
    pending: (totalTaskState as TaskStateStatistic)?.pending ?? 0,
    ready: (totalTaskState as TaskStateStatistic)?.ready ?? 0,
    // @ts-expect-error - TASK_WORKSTATE.PROGRESS is 'In Progress'
    progress: (totalTaskState as TaskStateStatistic)?.['in Progress'] ?? 0,
    completed: (totalTaskState as TaskStateStatistic)?.completed ?? 0,
    fail: (totalTaskState as TaskStateStatistic)?.fail ?? 0,
  };
  return (
    <VStack gap={1.5} className="w-full">
      <div className="text-headline-md font-bold text-blue-300">Overview</div>
      <div className="grid h-[190px] grid-cols-2 gap-[14px]">
        <VStack className="h-full w-full" gap={1}>
          <VFlex className="h-[87px] w-full" gap={1}>
            <VFlex
              gap={0.5}
              className="flex-1 rounded-md px-4 py-2 shadow-sm"
              items="center"
            >
              <VideoLabel className="size-8" />

              <VStack>
                <span className="text-body-lg text-gray-600">Total tasks</span>
                <span className="text-body-lg font-semibold text-black">
                  {totalTask}
                </span>
              </VStack>
            </VFlex>
          </VFlex>

          <VFlex className="h-[87px] w-full" gap={1}>
            <VFlex
              gap={0.5}
              className="flex-1 rounded-md px-4 py-2 shadow-sm"
              items="center"
            >
              <VideoLabel className="size-8" />

              <VStack>
                <span className="text-body-lg text-gray-600">Work Frames</span>
                <span className="text-body-lg font-semibold text-black">
                  {totalFrame}
                </span>
              </VStack>
            </VFlex>

            <VFlex
              gap={0.5}
              className="w-full flex-1 rounded-md px-4 py-2 shadow-sm"
              items="center"
            >
              <CenterFocusWeak className="size-8" />

              <VStack>
                <span className="text-body-lg text-gray-600">
                  Total Objects
                </span>
                <span className="text-body-lg font-semibold text-black">
                  {totalObject}
                </span>
              </VStack>
            </VFlex>
          </VFlex>
        </VStack>
        <VStack gap={0.5} className="rounded-md px-4 py-2 shadow-sm">
          <VFlex className="w-full justify-between">
            <VFlex className="items-center">
              <History className="size-5" />
              Total tasks progress
            </VFlex>

            <div className="text-body-sm text-gray-600">Total {totalTask}</div>
          </VFlex>
          <VFlex className="w-full justify-center">
            <DoughnutChart totalTask={totalTask} taskProgress={taskProgress} />
          </VFlex>
        </VStack>
      </div>
    </VStack>
  );
};

const MyWorkProjectPeriodLabeledObject = () => {
  const [periodUnit, setPeriodUnit] = useState<PeriodUnit>('day');
  const [between, setBetween] = useState<[Dayjs, Dayjs]>([
    dayjs(STATISTIC_CONST.TODAY).subtract(12, 'week'),
    dayjs(STATISTIC_CONST.TODAY),
  ]);

  const { data: taskMetric } = useMyWorkProjectPeriodMetricQuery({
    period: periodUnit,
    between,
  });

  const extendClassList = taskMetric?.extendClass?.filter(filterUnknownClass);

  const periodData = calculatePeriodData(
    extendClassList,
    taskMetric?.userMetric,
  );

  return (
    <VStack gap={0.5} className="h-full w-full">
      <div className="text-headline-md font-bold text-blue-300">
        Total labeled objects
      </div>

      <VFlex className="w-full" justify="between" items="center">
        <div className="text-headline-sm font-semibold text-primary-500">
          Trend
        </div>
        <VFlex items="center" className="w-full" justify="end" gap={0.5}>
          <RangePicker
            value={between}
            onChange={setBetween}
            periodUnit={periodUnit}
            key={periodUnit}
          />

          <VTab
            defaultValue="day"
            onValueChange={val => setPeriodUnit(val as PeriodUnit)}
            value={periodUnit}
            size="small"
            variant="secondary"
          >
            <VTabTriggerContainer>
              <VTabTrigger value="day">Daily</VTabTrigger>
              <VTabTrigger value="week">Weekly</VTabTrigger>
              <VTabTrigger value="month">Monthly</VTabTrigger>
            </VTabTriggerContainer>
          </VTab>
        </VFlex>
      </VFlex>

      {periodData.length > 0 && (
        <BarChart data={periodData} periodUnit={periodUnit} between={between} />
      )}
    </VStack>
  );
};

export default MyWorkProjectStatistics;
